import {
  getDashboardTournamentsControllerListQueryKey,
  getDashboardTournamentsControllerStatsQueryKey,
} from "@/api/__generated__/dashboard-tournaments/dashboard-tournaments";
import { MessageKey } from "@/i18n";
import {
  CompetitionLevel,
  Region,
  TournamentApprovalStatus,
  TournamentCompletionStatus,
  TournamentEffectiveStatus,
  TournamentStatus,
  TournamentTier,
} from "@/types";

// Approving, copying, and deleting a tournament all move rows between the
// stats buckets as well as the list, so both caches are bumped together.
export const TOURNAMENTS_INVALIDATE_KEYS = [
  getDashboardTournamentsControllerListQueryKey(),
  getDashboardTournamentsControllerStatsQueryKey(),
];

export const TOURNAMENT_EFFECTIVE_STATUS_LABELS: {
  [key in TournamentEffectiveStatus]: MessageKey;
} = {
  declined: "tournament.effectiveStatus.declined",
  upcoming: "tournament.effectiveStatus.upcoming",
  inactive: "tournament.effectiveStatus.inactive",
  inProgress: "tournament.effectiveStatus.inProgress",
  completed: "tournament.effectiveStatus.completed",
};

export const TOURNAMENT_COMPLETION_STATUS_LABELS: {
  [key in TournamentCompletionStatus]: MessageKey;
} = {
  inProgress: "tournament.completionStatus.inProgress",
  upcoming: "tournament.completionStatus.upcoming",
  completed: "tournament.completionStatus.completed",
};

export const TOURNAMENT_STATUS_LABELS: { [key in TournamentStatus]: MessageKey } = {
  active: "tournament.status.active",
  inactive: "tournament.status.inactive",
};

export const TOURNAMENT_APPROVAL_STATUS_LABELS: { [key in TournamentApprovalStatus]: MessageKey } =
  {
    pending: "tournament.approvalStatus.pending",
    confirmed: "tournament.approvalStatus.confirmed",
    declined: "tournament.approvalStatus.declined",
  };

export const TOURNAMENT_TIER_LABELS: { [key in TournamentTier]: MessageKey } = {
  NOT_SET: "tournament.enums.tier.NOT_SET",
  LOCAL: "tournament.enums.tier.LOCAL",
  REGIONAL: "tournament.enums.tier.REGIONAL",
  NATIONAL: "tournament.enums.tier.NATIONAL",
  INTERNATIONAL: "tournament.enums.tier.INTERNATIONAL",
};

export const COMPETITION_LEVEL_LABELS: { [key in CompetitionLevel]: MessageKey } = {
  WORLD_ADULT_CHAMPIONSHIP: "tournament.enums.competitionLevel.WORLD_ADULT_CHAMPIONSHIP",
  WORLD_YOUTH_CHAMPIONSHIP: "tournament.enums.competitionLevel.WORLD_YOUTH_CHAMPIONSHIP",
  WORLD_JUNIOR_CHAMPIONSHIP: "tournament.enums.competitionLevel.WORLD_JUNIOR_CHAMPIONSHIP",
  WORLD_CUP: "tournament.enums.competitionLevel.WORLD_CUP",
  WORLD_CUP_QUALIFICATION: "tournament.enums.competitionLevel.WORLD_CUP_QUALIFICATION",
  WORLD_CHAMPIONSHIP_QUALIFICATION:
    "tournament.enums.competitionLevel.WORLD_CHAMPIONSHIP_QUALIFICATION",
  CONTINENTAL_ADULT_CHAMPIONSHIP:
    "tournament.enums.competitionLevel.CONTINENTAL_ADULT_CHAMPIONSHIP",
  CONTINENTAL_YOUTH_CHAMPIONSHIP:
    "tournament.enums.competitionLevel.CONTINENTAL_YOUTH_CHAMPIONSHIP",
  CONTINENTAL_JUNIOR_CHAMPIONSHIP:
    "tournament.enums.competitionLevel.CONTINENTAL_JUNIOR_CHAMPIONSHIP",
  CONTINENTAL_CUP: "tournament.enums.competitionLevel.CONTINENTAL_CUP",
  CONTINENTAL_CHAMPIONSHIP_QUALIFICATION:
    "tournament.enums.competitionLevel.CONTINENTAL_CHAMPIONSHIP_QUALIFICATION",
  REGIONAL_LEVEL: "tournament.enums.competitionLevel.REGIONAL_LEVEL",
  NATIONAL_ADULT_CHAMPIONSHIP: "tournament.enums.competitionLevel.NATIONAL_ADULT_CHAMPIONSHIP",
  NATIONAL_YOUTH_CHAMPIONSHIP: "tournament.enums.competitionLevel.NATIONAL_YOUTH_CHAMPIONSHIP",
  NATIONAL_JUNIOR_CHAMPIONSHIP: "tournament.enums.competitionLevel.NATIONAL_JUNIOR_CHAMPIONSHIP",
  NATIONAL_UNIVERSITY_CHAMPIONSHIP:
    "tournament.enums.competitionLevel.NATIONAL_UNIVERSITY_CHAMPIONSHIP",
  NATIONAL_STUDENT_CHAMPIONSHIP: "tournament.enums.competitionLevel.NATIONAL_STUDENT_CHAMPIONSHIP",
  NATIONAL_UNIVERSIADE: "tournament.enums.competitionLevel.NATIONAL_UNIVERSIADE",
  NATIONAL_OLYMPIAD: "tournament.enums.competitionLevel.NATIONAL_OLYMPIAD",
  NATIONAL_CUP: "tournament.enums.competitionLevel.NATIONAL_CUP",
  OLYMPIC_GAMES_QUALIFICATION: "tournament.enums.competitionLevel.OLYMPIC_GAMES_QUALIFICATION",
  INTERNATIONAL_COMPETITION: "tournament.enums.competitionLevel.INTERNATIONAL_COMPETITION",
  SENIOR_VETERAN_COMPETITION: "tournament.enums.competitionLevel.SENIOR_VETERAN_COMPETITION",
  AMATEUR_SPORT_COMPETITION: "tournament.enums.competitionLevel.AMATEUR_SPORT_COMPETITION",
  OTHER_COUNTRY_LEVEL: "tournament.enums.competitionLevel.OTHER_COUNTRY_LEVEL",
  OTHER: "tournament.enums.competitionLevel.OTHER",
};

export const REGION_LABELS: { [key in Region]: MessageKey } = {
  LV_ADAZI_MUNICIPALITY: "tournament.enums.region.LV_ADAZI_MUNICIPALITY",
  LV_AIZKRAUKLE_MUNICIPALITY: "tournament.enums.region.LV_AIZKRAUKLE_MUNICIPALITY",
  LV_ALUKSNE_MUNICIPALITY: "tournament.enums.region.LV_ALUKSNE_MUNICIPALITY",
  LV_AUGSDAUGAVA_MUNICIPALITY: "tournament.enums.region.LV_AUGSDAUGAVA_MUNICIPALITY",
  LV_BALVI_MUNICIPALITY: "tournament.enums.region.LV_BALVI_MUNICIPALITY",
  LV_BAUSKA_MUNICIPALITY: "tournament.enums.region.LV_BAUSKA_MUNICIPALITY",
  LV_CESIS_MUNICIPALITY: "tournament.enums.region.LV_CESIS_MUNICIPALITY",
  LV_DAUGAVPILS_CITY: "tournament.enums.region.LV_DAUGAVPILS_CITY",
  LV_DIENVIDKURZEME_MUNICIPALITY: "tournament.enums.region.LV_DIENVIDKURZEME_MUNICIPALITY",
  LV_DOBELE_MUNICIPALITY: "tournament.enums.region.LV_DOBELE_MUNICIPALITY",
  LV_GULBENE_MUNICIPALITY: "tournament.enums.region.LV_GULBENE_MUNICIPALITY",
  LV_JEKABPILS_CITY: "tournament.enums.region.LV_JEKABPILS_CITY",
  LV_JELGAVA_CITY: "tournament.enums.region.LV_JELGAVA_CITY",
  LV_JELGAVA_MUNICIPALITY: "tournament.enums.region.LV_JELGAVA_MUNICIPALITY",
  LV_JURMALA_CITY: "tournament.enums.region.LV_JURMALA_CITY",
  LV_KEKAVA_MUNICIPALITY: "tournament.enums.region.LV_KEKAVA_MUNICIPALITY",
  LV_KRASLAVA_MUNICIPALITY: "tournament.enums.region.LV_KRASLAVA_MUNICIPALITY",
  LV_KULDIGA_MUNICIPALITY: "tournament.enums.region.LV_KULDIGA_MUNICIPALITY",
  LV_LIEPAJA_CITY: "tournament.enums.region.LV_LIEPAJA_CITY",
  LV_LIMBAZI_MUNICIPALITY: "tournament.enums.region.LV_LIMBAZI_MUNICIPALITY",
  LV_LIVANI_MUNICIPALITY: "tournament.enums.region.LV_LIVANI_MUNICIPALITY",
  LV_LUDZA_MUNICIPALITY: "tournament.enums.region.LV_LUDZA_MUNICIPALITY",
  LV_MADONA_MUNICIPALITY: "tournament.enums.region.LV_MADONA_MUNICIPALITY",
  LV_MARUPE_MUNICIPALITY: "tournament.enums.region.LV_MARUPE_MUNICIPALITY",
  LV_OGRE_MUNICIPALITY: "tournament.enums.region.LV_OGRE_MUNICIPALITY",
  LV_OLAINE_MUNICIPALITY: "tournament.enums.region.LV_OLAINE_MUNICIPALITY",
  LV_PREILI_MUNICIPALITY: "tournament.enums.region.LV_PREILI_MUNICIPALITY",
  LV_REZEKNE_CITY: "tournament.enums.region.LV_REZEKNE_CITY",
  LV_REZEKNE_MUNICIPALITY: "tournament.enums.region.LV_REZEKNE_MUNICIPALITY",
  LV_RIGA_CITY: "tournament.enums.region.LV_RIGA_CITY",
  LV_ROPAZI_MUNICIPALITY: "tournament.enums.region.LV_ROPAZI_MUNICIPALITY",
  LV_SALASPILS_MUNICIPALITY: "tournament.enums.region.LV_SALASPILS_MUNICIPALITY",
  LV_SALDUS_MUNICIPALITY: "tournament.enums.region.LV_SALDUS_MUNICIPALITY",
  LV_SAULKRASTI_MUNICIPALITY: "tournament.enums.region.LV_SAULKRASTI_MUNICIPALITY",
  LV_SIGULDA_MUNICIPALITY: "tournament.enums.region.LV_SIGULDA_MUNICIPALITY",
  LV_SMILTENE_MUNICIPALITY: "tournament.enums.region.LV_SMILTENE_MUNICIPALITY",
  LV_TALSI_MUNICIPALITY: "tournament.enums.region.LV_TALSI_MUNICIPALITY",
  LV_TUKUMS_MUNICIPALITY: "tournament.enums.region.LV_TUKUMS_MUNICIPALITY",
  LV_VALKA_MUNICIPALITY: "tournament.enums.region.LV_VALKA_MUNICIPALITY",
  LV_VALMIERA_CITY: "tournament.enums.region.LV_VALMIERA_CITY",
  LV_VENTSPILS_CITY: "tournament.enums.region.LV_VENTSPILS_CITY",
  LV_VENTSPILS_MUNICIPALITY: "tournament.enums.region.LV_VENTSPILS_MUNICIPALITY",
};
